import { Component, effect, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { Meta } from '@angular/platform-browser';
import { NavigationEnd, NavigationCancel, NavigationError, ResolveStart, Router, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs/operators';

import { AuthService } from '@core/auth.service';
import { InactivityService } from '@core/inactivity.service';
import { PortalTypeService } from '@core/multi-tenant-auth';
import { SystemService } from '@core/system.service';
import { SessionTimeoutWarningDialogComponent } from '@shared/components/session-timeout-warning-dialog/session-timeout-warning-dialog.component';
import { OverlayLoadingService } from '@shared/loading/loading-overlay-manager';

import { DevModeComponent } from './features/dev-tool/dev-tool.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  imports: [
    RouterOutlet,
    DevModeComponent,
  ],
})
export class AppComponent implements OnInit {
  iconRegistry: MatIconRegistry = inject(MatIconRegistry);

  private readonly meta = inject(Meta);
  private readonly systemService = inject(SystemService);
  private readonly router = inject(Router);
  private readonly overlay = inject(OverlayLoadingService);
  private readonly authService = inject(AuthService);
  private readonly inactivityService = inject(InactivityService);
  private readonly dialog = inject(MatDialog);
  private readonly portalTypeService = inject(PortalTypeService);
  private readonly translate = inject(TranslateService);

  private showTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private dialogRef: MatDialogRef<SessionTimeoutWarningDialogComponent> | null = null;

  private readonly isNavigating = toSignal(
    this.router.events.pipe(
      map((event) => {
        if (event instanceof ResolveStart) {
          // `ResolveStart` occurs after guards run, as some guards show confirmation dialogs
          return true;
        } else if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
          return false;
        }
        return undefined;
      }),
      filter((value): value is boolean => value !== undefined)
    ),
    { initialValue: false }
  );

  constructor() {
    this.overlay.setAriaLabel('common.preloader');

    effect(() => {
      if (this.isNavigating()) {
        this.showTimeoutId ??= this.overlay.showWithDelay();
      } else {
        this.overlay.hide(this.showTimeoutId);
        this.showTimeoutId = null;
      }
    });

    effect(() => {
      if (this.inactivityService.warningCountdown() !== null && this.dialogRef === null) {
        this.dialogRef = this.dialog.open(SessionTimeoutWarningDialogComponent, {
          disableClose: true,
        });
        this.dialogRef.afterClosed().subscribe(() => {
          this.dialogRef = null;
        });
      }
    });

    effect(() => {
      const sha = this.systemService.commitSha();
      if (sha) {
        this.meta.updateTag({ name: 'git-commit-sha', content: sha });
      }
    });

    const descriptionKey = `${this.portalTypeService.getPortalTypeTranslationKey()}.application.description`;
    this.translate.get(descriptionKey).subscribe((description: string) => {
      this.meta.updateTag({ name: 'description', content: description });
    });
  }

  ngOnInit(): void {
    this.iconRegistry.setDefaultFontSetClass('material-symbols-outlined');

    const authState$ = this.authService.isAuthenticated();
    authState$.pipe(filter((isAuthenticated) => isAuthenticated === true)).subscribe(() => this.inactivityService.start());
    authState$.pipe(filter((isAuthenticated) => isAuthenticated === false)).subscribe(() => this.inactivityService.stop());
  }
}
